import { isArchivedAssignment } from '../../../utils/assignmentHistory';

// Bir çalışanın projedeki tüm atamalarını kalıcı olarak silme yetkisi yalnızca
// ana admindedir (ProjectsManagementPage'deki isMainAdmin ile aynı kontrol).
export const MAIN_ADMIN_EMAIL = 'turanemre1999@gmail.com';

// Roller kayıtta Türkçe adlarıyla tutulur (`name`); ekranda `id` ile çevrilir
export const ROLE_OPTIONS = [
  { id: 'backup-personeli', name: 'Backup Personeli' },
  { id: 'cra', name: 'CRA' },
  { id: 'calisma-baslatma', name: 'Çalışma Başlatma Sorumlusu' },
  { id: 'egitim-sorumlusu', name: 'Eğitim Sorumlusu' },
  { id: 'finans-uzmani', name: 'Finans Uzmanı' },
  { id: 'ik-uzmani', name: 'IK Uzmanı' },
  { id: 'it-uzmani', name: 'IT Uzmanı' },
  { id: 'is-gelistirme', name: 'İş Geliştirme Uzmanı' },
  { id: 'kalite-muduru', name: 'Kalite Müdürü' },
  { id: 'klinik-hemsire', name: 'Klinik Araştırma Hemşiresi' },
  { id: 'proje-muduru', name: 'Proje Müdürü' },
  { id: 'saha-koordinatoru', name: 'Saha Koordinatörü' },
  { id: 'saha-koordinatoru-sorumlusu', name: 'Saha Koordinatörü Sorumlusu' },
  { id: 'smo-lead', name: 'SMO Lead' },
];

// Bir merkezde bu rollerden yalnızca bir kişi olabilir
export const MANAGER_ROLES = ['Saha Koordinatörü Sorumlusu', 'Proje Müdürü'];

// Test projelerinde tarife seçilmezse kullanılan varsayılan
export const DEFAULT_TEST_TARIFF = {
  id: 'test-tariff-default',
  type: 'FTE',
  value: '0.7',
  price: 700,
  currency: 'TRY',
};

/** Rol adının geçerli dildeki karşılığı; listede olmayan ad olduğu gibi. */
export const roleLabel = (t, name) => {
  const option = ROLE_OPTIONS.find((role) => role.name === name);
  return option ? t(`projectManagement.roles.${option.id}`) : name;
};

/** Atanma tarihi (Firestore Timestamp, Date ya da metin) gün.ay.yıl. */
export const formatAssignedDate = (timestamp, locale = 'tr-TR') => {
  if (!timestamp) return '';
  if (timestamp.seconds) return new Date(timestamp.seconds * 1000).toLocaleDateString(locale);
  if (timestamp instanceof Date) return timestamp.toLocaleDateString(locale);
  return timestamp;
};

/** Ayrılma tarihi (`unassignedAt`, ISO metni ya da Timestamp) gün.ay.yıl; okunamazsa boş. */
export const formatLeftDate = (entry, locale = 'tr-TR') => {
  const raw = entry?.unassignedAt;
  if (!raw) return '';
  const date = raw?.toDate ? raw.toDate() : new Date(raw);
  return Number.isNaN(date.getTime()) ? '' : date.toLocaleDateString(locale);
};

/** Merkez seçeneklerinin tekilleştirme anahtarı (aynı hastane için birden çok Centers dokümanı var). */
export const centerDedupeLabel = (center) => `${center.Adı || ''} - ${center.Bilgi || ''} - ${center.isDeleted ? 'deleted' : ''}`;

const CENTER_STATUS_ORDER = { active: 0, passive: 1, left: 2 };

/**
 * Çalışanın atamaları merkez merkez: o merkezdeki roller, tip / kör durumu ve
 * merkezdeki durumu (ayrıldıysa ayrılma kaydıyla). Ayrılınan merkezler sonda.
 */
export const assignmentsByCenter = (groupedWorker) => {
  const byCenter = new Map();
  groupedWorker.centers.forEach((row) => {
    const key = row.centerId || '';
    if (!byCenter.has(key)) byCenter.set(key, { centerId: row.centerId, centerName: row.centerName, rows: [] });
    const group = byCenter.get(key);
    if (!group.centerName && row.centerName) group.centerName = row.centerName;
    group.rows.push(row);
  });
  return [...byCenter.values()]
    .map((group) => {
      const activeRows = group.rows.filter((row) => !isArchivedAssignment(row));
      const shownRows = activeRows.length > 0 ? activeRows : group.rows;
      let status = 'active';
      if (activeRows.length === 0) status = 'left';
      else if (activeRows.every((row) => row.status === 'passive')) status = 'passive';
      return {
        ...group,
        status,
        roles: [...new Set(shownRows.map((row) => row.role).filter(Boolean))],
        workerType: shownRows.find((row) => row.workerType)?.workerType || '',
        blindStatus: shownRows.find((row) => row.blindStatus)?.blindStatus || '',
        leftEntry: activeRows.length === 0 ? group.rows.find(isArchivedAssignment) : null,
      };
    })
    .sort((a, b) => CENTER_STATUS_ORDER[a.status] - CENTER_STATUS_ORDER[b.status]);
};

/**
 * Bir çalışanın projedeki satırlarının özeti. Ayrılınan merkezler kayıtta
 * kalır; özetlerde aktif olanlar sayılır, ayrılanlar ayrıca gösterilir.
 */
export const summarizeWorker = (groupedWorker) => {
  const activeRows = groupedWorker.centers.filter((c) => !isArchivedAssignment(c));
  const activeCenterIds = new Set(activeRows.map((c) => c.centerId));
  const archivedByCenter = [];
  groupedWorker.centers
    .filter((c) => isArchivedAssignment(c) && !activeCenterIds.has(c.centerId))
    .forEach((c) => {
      if (!archivedByCenter.some((a) => a.centerId === c.centerId)) archivedByCenter.push(c);
    });
  const activeCenters = [];
  activeRows.forEach((c) => {
    if (!activeCenters.some((a) => a.centerId === c.centerId)) activeCenters.push(c);
  });
  const uniqueRoles = [...new Set(activeRows.map((c) => c.role).filter(Boolean))];
  const hasLeftAllCenters = groupedWorker.centers.length > 0 && activeRows.length === 0;
  const allPassive = activeRows.length > 0 && activeRows.every((c) => c.status === 'passive');
  let status = 'active';
  if (hasLeftAllCenters) status = 'left';
  else if (allPassive) status = 'passive';
  return { activeRows, activeCenters, activeCenterIds, archivedByCenter, uniqueRoles, hasLeftAllCenters, allPassive, status };
};
